import { useEffect, useState } from 'react';
import { useProductsStore } from '@/store/products';
import { Product, StockStatus } from '@/lib/constants';
import { Plus, Search, Package, DollarSign, Eye, EyeOff, Edit2, Trash2, Image as ImageIcon } from 'lucide-react';
import { ProductImageModal } from '@/components/products/ProductImageModal';
import { useToastStore } from '@/store/toasts';
import { logger } from '@/lib/logger';

export default function MenuPage() {
  const {
    products,
    isLoading,
    fetchProducts,
    addProduct,
    updateProduct,
    deleteProduct,
    toggleProduct,
    updateStock,
  } = useProductsStore();

  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [showArchived, setShowArchived] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [imageModalProduct, setImageModalProduct] = useState<Product | null>(null); // Sprint 4I

  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  // Get unique categories
  const categories = Array.from(new Set(products.map((p) => p.category).filter(Boolean)));

  // Filter products
  const filteredProducts = products
    .filter((product) => {
      // Hide deleted/inactive products by default unless "Show Archived" is checked
      if (!showArchived && !product.is_active) {
        return false;
      }

      const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = filterCategory === 'all' || product.category === filterCategory;
      const matchesStatus =
        filterStatus === 'all' ||
        (filterStatus === 'active' && product.is_active) ||
        (filterStatus === 'inactive' && !product.is_active);

      return matchesSearch && matchesCategory && matchesStatus;
    })
    .sort((a, b) => {
      // If viewing all categories, group by category first, then alphabetically
      if (filterCategory === 'all') {
        const catA = a.category || 'Uncategorized';
        const catB = b.category || 'Uncategorized';
        
        if (catA !== catB) {
          return catA.localeCompare(catB);
        }
      }
      
      // Then sort alphabetically by name within each category
      return a.name.localeCompare(b.name);
    });

  const handleAddProduct = () => {
    setEditingProduct(null);
    setIsAddModalOpen(true);
  };

  const handleEditProduct = (product: Product) => {
    setEditingProduct(product);
    setIsAddModalOpen(true);
  };

  const addToast = useToastStore((state) => state.addToast);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [retryAction, setRetryAction] = useState<{ type: string; id: string } | null>(null);

  const handleDeleteProduct = async (productId: string) => {
    setDeleteConfirmId(productId);
  };

  const confirmDeleteProduct = async () => {
    if (!deleteConfirmId) return;
    
    try {
      await deleteProduct(deleteConfirmId);
      addToast({ type: 'success', message: 'Product deleted successfully' });
    } catch (error) {
      logger.error('Failed to delete product', error as Error);
      addToast({ type: 'error', message: 'Failed to delete product. Please try again.' });
      setRetryAction({ type: 'delete', id: deleteConfirmId });
    } finally {
      setDeleteConfirmId(null);
    }
  };

  const handleToggleProduct = async (productId: string) => {
    try {
      await toggleProduct(productId);
      addToast({ type: 'success', message: 'Product visibility updated' });
    } catch (error) {
      logger.error('Failed to toggle product', error as Error);
      addToast({ type: 'error', message: 'Failed to toggle product. Please try again.' });
      setRetryAction({ type: 'toggle', id: productId });
    }
  };

  const handleStockChange = async (productId: string, stockStatus: StockStatus) => {
    try {
      await updateStock(productId, stockStatus);
      addToast({ type: 'success', message: 'Stock updated successfully' });
    } catch (error) {
      logger.error('Failed to update stock', error as Error);
      addToast({ type: 'error', message: 'Failed to update stock. Please try again.' });
      setRetryAction({ type: 'stock', id: productId });
    }
  };

  // handleRetry can be added to a retry button in error UI if needed
  const _handleRetry = async () => {
    if (!retryAction) return;
    
    if (retryAction.type === 'delete') {
      await confirmDeleteProduct();
    } else if (retryAction.type === 'toggle') {
      await handleToggleProduct(retryAction.id);
    }
    setRetryAction(null);
  };
  void _handleRetry; // suppress unused warning

  const getStockBadgeClass = (status: string | null) => {
    switch (status) {
      case 'in_stock':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'low_stock':
        return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      case 'out_of_stock':
        return 'bg-red-50 text-red-700 border-red-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Menu & Inventory</h1>
              <p className="text-muted-foreground">
                Manage your products, prices, and stock levels
              </p>
            </div>

            <button
              onClick={handleAddProduct}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
            >
              <Plus className="w-5 h-5" />
              <span>Add Product</span>
            </button>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                  <Package className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Active Products</p>
                  <p className="text-2xl font-bold text-foreground">
                    {products.filter((p) => p.is_active).length}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gray-50 rounded-lg flex items-center justify-center">
                  <EyeOff className="w-5 h-5 text-gray-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Archived</p>
                  <p className="text-2xl font-bold text-foreground">
                    {products.filter((p) => !p.is_active).length}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-yellow-50 rounded-lg flex items-center justify-center">
                  <Package className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Low Stock</p>
                  <p className="text-2xl font-bold text-foreground">
                    {products.filter((p) => p.is_active && p.stock_status === 'low_stock').length}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center">
                  <Package className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Out of Stock</p>
                  <p className="text-2xl font-bold text-foreground">
                    {products.filter((p) => p.is_active && p.stock_status === 'out_of_stock').length}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              {/* Category Filter */}
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="px-4 py-2 border border-border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="all">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat || ''}>
                    {cat || 'Uncategorized'}
                  </option>
                ))}
              </select>

              {/* Status Filter */}
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-2 border border-border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            {/* Show Archived Toggle */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="showArchived"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
                className="w-4 h-4 text-primary border-border rounded focus:ring-2 focus:ring-primary"
              />
              <label htmlFor="showArchived" className="text-sm text-muted-foreground cursor-pointer">
                Show archived/deleted products ({products.filter((p) => !p.is_active).length})
              </label>
            </div>
          </div>
        </div>

        {/* Products Grid */}
        {isLoading ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Loading products...</p>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="bg-card border border-border rounded-xl p-12 text-center">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Package className="w-8 h-8 text-primary" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">No Products Found</h3>
            <p className="text-muted-foreground mb-4">
              {searchQuery || filterCategory !== 'all' || filterStatus !== 'all'
                ? 'Try adjusting your filters'
                : 'Get started by adding your first product'}
            </p>
            {!searchQuery && filterCategory === 'all' && filterStatus === 'all' && (
              <button
                onClick={handleAddProduct}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
              >
                <Plus className="w-4 h-4" />
                Add First Product
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProducts.map((product) => (
              <div
                key={product.id}
                className={`bg-card border rounded-xl p-6 hover:shadow-lg transition-shadow ${
                  !product.is_active ? 'opacity-60' : ''
                }`}
              >
                {/* Product Image Thumbnail (Sprint 4I) */}
                {product.primaryImage && (
                  <div className="mb-4">
                    <img
                      src={product.primaryImage.smUrl}
                      alt={product.name}
                      className="w-full h-40 object-cover rounded-lg"
                    />
                    {product.galleryImages && product.galleryImages.length > 0 && (
                      <div className="mt-2 text-xs text-gray-500 flex items-center gap-1">
                        <ImageIcon className="w-3 h-3" />
                        +{product.galleryImages.length} more
                      </div>
                    )}
                  </div>
                )}
                
                {/* Product Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-lg font-semibold text-foreground">
                        {product.name}
                      </h3>
                      {!product.is_active && (
                        <span className="inline-block text-xs font-medium text-red-600 bg-red-50 px-2 py-1 rounded border border-red-200">
                          ARCHIVED
                        </span>
                      )}
                    </div>
                    {product.category && (
                      <span className="inline-block text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
                        {product.category}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => handleToggleProduct(product.id)}
                    className={`p-2 rounded-lg ${
                      product.is_active
                        ? 'bg-green-50 text-green-600 hover:bg-green-100'
                        : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                    }`}
                    title={product.is_active ? 'Active' : 'Inactive'}
                  >
                    {product.is_active ? (
                      <Eye className="w-4 h-4" />
                    ) : (
                      <EyeOff className="w-4 h-4" />
                    )}
                  </button>
                </div>

                {/* Description */}
                {product.description && (
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                    {product.description}
                  </p>
                )}

                {/* Price & Unit */}
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="w-4 h-4 text-primary" />
                  <span className="text-xl font-bold text-foreground">
                    ₱{parseFloat(product.price_today).toFixed(2)}
                  </span>
                  <span className="text-sm text-muted-foreground">/ {product.unit}</span>
                </div>
                {/* Sprint 27: Price explanation */}
                <p className="text-xs text-muted-foreground mb-4">
                  You receive 100% of your listed price.
                </p>

                {/* Stock Status */}
                <div className="mb-4">
                  <select
                    value={product.stock_status || 'in_stock'}
                    onChange={(e) => handleStockChange(product.id, e.target.value as StockStatus)}
                    className={`w-full px-3 py-2 rounded-lg text-sm font-medium border ${getStockBadgeClass(
                      product.stock_status
                    )}`}
                  >
                    <option value="in_stock">In Stock</option>
                    <option value="low_stock">Low Stock</option>
                    <option value="out_of_stock">Out of Stock</option>
                  </select>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <button
                    onClick={() => setImageModalProduct(product)}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 border border-border rounded-lg text-sm font-medium hover:bg-blue-50 dark:hover:bg-blue-950 transition-colors"
                    title="Manage product images"
                  >
                    <ImageIcon className="w-4 h-4" />
                    Images
                  </button>
                  <button
                    onClick={() => handleEditProduct(product)}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 border border-border rounded-lg text-sm font-medium hover:bg-muted transition-colors"
                  >
                    <Edit2 className="w-4 h-4" />
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeleteProduct(product.id)}
                    className="px-4 py-2 border border-red-200 text-red-600 rounded-lg text-sm font-medium hover:bg-red-50 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Modal - Will be implemented in next step */}
      {isAddModalOpen && (
        <ProductFormModal
          product={editingProduct}
          onClose={() => {
            setIsAddModalOpen(false);
            setEditingProduct(null);
          }}
          onSave={async (data) => {
            try {
              if (editingProduct) {
                await updateProduct(editingProduct.id, data);
              } else {
                await addProduct(data as any);
              }
              setIsAddModalOpen(false);
              setEditingProduct(null);
            } catch (error) {
              // Error handled in store
            }
          }}
        />
      )}
      
      {/* Image Manager Modal (Sprint 4I) */}
      {imageModalProduct && (
        <ProductImageModal
          product={imageModalProduct}
          isOpen={true}
          onClose={() => setImageModalProduct(null)}
        />
      )}
    </div>
  );
}

// Product Form Modal Component
function ProductFormModal({
  product,
  onClose,
  onSave,
}: {
  product: Product | null;
  onClose: () => void;
  onSave: (data: Partial<Product>) => Promise<void>;
}) {
  const [formData, setFormData] = useState({
    name: product?.name || '',
    description: product?.description || '',
    category: product?.category || '',
    unit: product?.unit || 'kg',
    price_today: product?.price_today || '',
    stock_status: product?.stock_status || 'in_stock',
  });
  const [isSaving, setIsSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      await onSave(formData);
    } catch (error) {
      alert('Failed to save product. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-xl max-w-md w-full p-6">
        <h2 className="text-2xl font-bold text-foreground mb-4">
          {product ? 'Edit Product' : 'Add New Product'}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Product Name *
            </label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:!text-white focus:outline-none focus:ring-2 focus:ring-primary [color-scheme:light] dark:[color-scheme:dark]"
              placeholder="e.g., Fresh Tomatoes"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:!text-white focus:outline-none focus:ring-2 focus:ring-primary [color-scheme:light] dark:[color-scheme:dark]"
              placeholder="Describe your product..."
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Category
            </label>
            <input
              type="text"
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="w-full px-3 py-2 border border-border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:!text-white focus:outline-none focus:ring-2 focus:ring-primary [color-scheme:light] dark:[color-scheme:dark]"
              placeholder="e.g., Vegetables, Fruits"
            />
          </div>

          {/* Price & Unit */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Your Price (₱) *
              </label>
              <input
                type="number"
                required
                step="0.01"
                min="0"
                value={formData.price_today}
                onChange={(e) => setFormData({ ...formData, price_today: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:!text-white focus:outline-none focus:ring-2 focus:ring-primary [color-scheme:light] dark:[color-scheme:dark]"
                placeholder="0.00"
              />
              <p className="text-xs text-muted-foreground mt-1">
                You receive 100% of this price.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Unit *
              </label>
              <select
                required
                value={formData.unit}
                onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:!text-white focus:outline-none focus:ring-2 focus:ring-primary [color-scheme:light] dark:[color-scheme:dark]"
              >
                <option value="kg">kg</option>
                <option value="g">g</option>
                <option value="pc">piece</option>
                <option value="pack">pack</option>
                <option value="bundle">bundle</option>
              </select>
            </div>
          </div>

          {/* Stock Status */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Stock Status
            </label>
            <select
              value={formData.stock_status}
              onChange={(e) => setFormData({ ...formData, stock_status: e.target.value })}
              className="w-full px-3 py-2 border border-border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:!text-white focus:outline-none focus:ring-2 focus:ring-primary [color-scheme:light] dark:[color-scheme:dark]"
            >
              <option value="in_stock">In Stock</option>
              <option value="low_stock">Low Stock</option>
              <option value="out_of_stock">Out of Stock</option>
            </select>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className="flex-1 px-4 py-2 border border-border rounded-lg font-medium hover:bg-muted disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 disabled:opacity-50"
            >
              {isSaving ? 'Saving...' : product ? 'Update' : 'Add Product'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
